<template>
  <div class="warning-block d-flex align-start py-1 px-4" data-testid="vpn-creation-failed-block">
    <v-icon class="mr-2" icon="$warning" />
    <div class="d-flex flex-column align-start">
      <span class="body2" data-testid="vpn-creation-failed-description">
        {{ failureDescription }}
      </span>
      <a
        class="subtitle2 text-decoration-underline cursor-pointer mt-1 retry-link"
        :class="{ 'retry-link--disabled': actionRestricted || retrying }"
        role="button"
        data-testid="retry-vpn-creation-link"
        @click="onRetry"
      >
        {{ $t("general.retry") }}
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useVirtualOfficeStore } from "@/_store/network/virtual-office.module";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";

export default defineComponent({
  setup() {
    const virtualOfficeStore = useVirtualOfficeStore();
    const { vpnConnectionStatus } = storeToRefs(virtualOfficeStore);
    const { t, te } = useI18n();
    const retrying = ref(false);

    const failureDescription = computed(() => {
      const reason = vpnConnectionStatus.value.failureReason;
      if (!reason) return t("network.creationFailed.description");
      const key = `network.creationFailed.reasons.${reason}`;
      return te(key) ? t(key) : reason;
    });

    const actionRestricted = computed(
      () =>
        isWorkspaceFrozenOrActionRestricted(
          RolePermissionsScope.PROTECTION,
          SubscriptionModule.NETWORK
        ) &&
        isWorkspaceFrozenOrActionRestricted(RolePermissionsScope.PROTECTION, SubscriptionModule.SWG)
    );

    const onRetry = async () => {
      if (retrying.value || actionRestricted.value) return;
      retrying.value = true;
      try {
        await virtualOfficeStore.retryVirtualOffice();
      } finally {
        retrying.value = false;
      }
    };

    return {
      vpnConnectionStatus,
      failureDescription,
      retrying,
      actionRestricted,
      onRetry,
    };
  },
});
</script>

<style lang="scss" scoped>
.retry-link {
  color: rgb(var(--v-theme-yellow-base)) !important;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
</style>
